import { InfoMenu } from "@src/components/elements/InfoMenu";
import { SelectOption } from "@src/components/elements/Select/SelectOption";
import { SelectStyledRoot } from "@src/components/elements/Select/SelectStyledRoot";
import React, { ReactElement, Ref } from "react";
import ReactSelectAsync from "react-select/async";
import ReactSelectAsyncCreatable from "react-select/async-creatable";
import Select from "react-select/dist/declarations/src/Select";
import {
  MultiSelectProps,
  multiStyles,
} from "@src/components/elements/Select/MultiSelect";
import { GroupBase, OptionsOrGroups } from "react-select";
import _ from "lodash";

export const AsyncMultiSelect: React.FC<
  Omit<MultiSelectProps, "options"> & {
    loadOptions: (
      inputValue: string,
    ) => Promise<OptionsOrGroups<SelectOption, GroupBase<SelectOption>>>;
    defaultOptions:
      | boolean
      | OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
    onLoadMore?: () => void;
  }
> = React.forwardRef(
  (
    {
      inputRef,
      isCreatable,
      onCreateOption,
      isClearable,
      isLoading,
      placeholder,
      error,
      disableError,
      size,
      isDisabled,
      onChange,
      value,
      menuPortalTarget,
      horizontalLabel,
      className,
      label,
      required,
      tooltipText,
      dropdownClassName,
      defaultOptions,
      loadOptions,
      labelType = "normal",
      components,
      onLoadMore,
    },
    ref: Ref<Select<SelectOption, true>>,
  ): ReactElement => {
    const styles = multiStyles(error, size, labelType);
    const [isFocused, setIsFocused] = React.useState(false);
    const labelShouldFloat =
      labelType === "floating" && (isFocused || !!value?.length);

    const debounced = React.useMemo(() => {
      return _.debounce(
        (
          search: string,
          cb: (
            options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>,
          ) => void,
        ) => {
          void loadOptions(search).then((r) => {
            cb(r);
          });
        },
        1000,
      );
    }, [loadOptions]);

    return (
      <SelectStyledRoot
        labelShouldFloat={labelShouldFloat}
        horizontalLabel={horizontalLabel}
        className={className}
      >
        {label && labelType === "normal" && (
          <div className="label-wrap">
            <label htmlFor={label}>
              {label} {required && <span className="error">*</span>}{" "}
            </label>
            {tooltipText && <InfoMenu title={<span>{tooltipText}</span>} />}
          </div>
        )}
        <div className="input-container">
          {isCreatable ? (
            <ReactSelectAsyncCreatable
              ref={inputRef || ref}
              isClearable={isClearable}
              isMulti={true}
              value={value}
              isLoading={isLoading}
              placeholder={placeholder ?? "Select or create..."}
              styles={styles}
              isDisabled={isDisabled}
              menuShouldBlockScroll={true}
              onChange={onChange}
              menuPortalTarget={menuPortalTarget}
              className={`select-input ${dropdownClassName}`}
              defaultOptions={defaultOptions}
              loadOptions={debounced}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onCreateOption={onCreateOption}
              components={components}
              onMenuScrollToBottom={onLoadMore}
              menuPlacement="auto"
              closeMenuOnSelect={false}
            />
          ) : (
            <ReactSelectAsync
              ref={inputRef || ref}
              isClearable={isClearable}
              isMulti={true}
              value={value}
              isLoading={isLoading}
              placeholder={placeholder}
              styles={styles}
              isDisabled={isDisabled}
              menuShouldBlockScroll={true}
              onChange={onChange}
              menuPortalTarget={menuPortalTarget}
              className={`select-input ${dropdownClassName}`}
              defaultOptions={defaultOptions}
              loadOptions={debounced}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              components={components}
              onMenuScrollToBottom={onLoadMore}
              menuPlacement="auto"
              closeMenuOnSelect={false}
            />
          )}
          {labelType === "floating" ? (
            <div className="label-wrap">
              <label className="floating-label">
                {label} {required && <span className="error">*</span>}
              </label>
            </div>
          ) : null}
          {disableError ? null : (
            <span className="error">{error ?? <>&nbsp;</>}</span>
          )}
        </div>
      </SelectStyledRoot>
    );
  },
);

AsyncMultiSelect.displayName = "AsyncMultiSelect";
